import { formatDate } from "@/lib/utils";
import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  FileText,
  Download,
  Filter,
  Users,
  Store,
  Calendar,
  Layers,
  CheckCircle,
  Clock,
  Printer as PrintIcon,
  RefreshCcw,
  Loader2,
  DollarSign,
  PieChart,
  Activity,
  Receipt,
  Search,
  TrendingUp,
  Calculator,
  Percent,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ReactSelect, AsyncReactSelect } from "@/components/ui/react-select";

import { Badge } from "@/components/ui/badge";

export const SalesInvoiceReport = () => {
  const currentStore = useSelector((state) => state.current_store);
  const currencySymbol = currentStore?.default_currency?.symbol || "";

  const [filters, setFilters] = useState(() => ({
    customer_id: "",
    store_id: currentStore?.id ? String(currentStore.id) : "",
    start_date: "",
    end_date: "",
    min_total: "",
    max_total: "",
    invoice_number: "",
    status: "",
    payment_status: "",
    sort_by: "date",
    sort_order: "desc",
  }));
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const prevStoreIdRef = useRef(currentStore?.id);

  useEffect(() => {
    if (currentStore?.id && currentStore.id !== prevStoreIdRef.current) {
      prevStoreIdRef.current = currentStore.id;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFilters((prev) => ({
        ...prev,
        store_id: String(currentStore.id),
      }));
    }
  }, [currentStore?.id]);



  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    if (!permission) return true;
    return permissions.includes(permission);
  };

  const showToast = (message, type) => {
    if (type === "error") toast.error(message);
    else if (type === "success") toast.success(message);
    else toast(message);
  };

  const summaryStats = previewData?.summary || null;

  // ==================== QUERIES ====================
  const loadCustomerOptions = async (inputValue) => {
    try {
      const params = { perPage: 15 };
      if (inputValue) {
        params.q = String(inputValue);
      }
      if (filters.store_id) {
        params.registration_store_id = filters.store_id;
      }

      const response = await apiClient.get(`/customer`, {
        params,
      });
      const customersData = response.data.data || [];
      return customersData.map((customer) => ({
        value: String(customer.id),
        label:
          customer.customer_type !== "individual" && customer.company
            ? customer.company
            : customer.full_name,
      }));
    } catch (error) {
      console.error("Error loading customers:", error);
      return [];
    }
  };

  const { data: stores = [] } = useQuery({
    enabled: hasPermission("report sales invoice list view"),
    queryKey: ["stores"],
    queryFn: async () => {
      const response = await apiClient.get(`/store`);
      return response.data.data;
    },
  });

  // ==================== MUTATIONS ====================
  const previewReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report sales invoice list view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.customer_id)
        params.customer_id = currentFilters.customer_id;
      if (currentFilters.store_id) params.store_id = currentFilters.store_id;
      if (currentFilters.start_date)
        params.start_date = new Date(currentFilters.start_date).toISOString();
      if (currentFilters.end_date)
        params.end_date = new Date(currentFilters.end_date).toISOString();
      if (currentFilters.min_total !== "")
        params.min_total = Number(currentFilters.min_total);
      if (currentFilters.max_total !== "")
        params.max_total = Number(currentFilters.max_total);
      if (currentFilters.invoice_number)
        params.invoice_number = currentFilters.invoice_number;
      if (currentFilters.status) params.status = currentFilters.status;
      if (currentFilters.payment_status)
        params.payment_status = currentFilters.payment_status;
      if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
      if (currentFilters.sort_order)
        params.sort_order = currentFilters.sort_order;

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.DATA.SALES_INVOICE,
        { params },
      );
      return response.data;
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setShowFilters(false);
      showToast("Report preview loaded successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message || "Failed to load report preview",
        "error",
      );
    },
  });

  const generateReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report sales invoice list view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.customer_id)
        params.customer_id = currentFilters.customer_id;
      if (currentFilters.store_id) params.store_id = currentFilters.store_id;
      if (currentFilters.start_date)
        params.start_date = new Date(currentFilters.start_date).toISOString();
      if (currentFilters.end_date)
        params.end_date = new Date(currentFilters.end_date).toISOString();
      if (currentFilters.min_total !== "")
        params.min_total = Number(currentFilters.min_total);
      if (currentFilters.max_total !== "")
        params.max_total = Number(currentFilters.max_total);
      if (currentFilters.invoice_number)
        params.invoice_number = currentFilters.invoice_number;
      if (currentFilters.status) params.status = currentFilters.status;
      if (currentFilters.payment_status)
        params.payment_status = currentFilters.payment_status;
      if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
      if (currentFilters.sort_order)
        params.sort_order = currentFilters.sort_order;

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.SALES_INVOICE_REPORT,
        {
          params,
          responseType: "blob",
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      const blob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setShowModal(true);
      showToast("Sales invoice report generated successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message ||
        "Failed to generate sales invoice report",
        "error",
      );
    },
  });

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleGenerateReport = () => {
    showToast("Generating sales invoice report...", "info");
    generateReportMutation.mutate(filters);
  };

  const handleClearFilters = () => {
    setShowFilters(true);
    setPreviewData(null);
    setFilters({
      customer_id: "",
      store_id: currentStore?.id ? String(currentStore.id) : "",
      start_date: "",
      end_date: "",
      min_total: "",
      max_total: "",
      invoice_number: "",
      status: "",
      payment_status: "",
      sort_by: "date",
      sort_order: "desc",
    });
    setSelectedCustomer(null);
  };

  const handleDownloadPDF = () => {
    if (pdfBlobUrl) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `sales-invoice-report-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast("PDF downloaded successfully!", "success");
    }
  };

  const handlePrintPDF = () => {
    if (pdfBlobUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfBlobUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      showToast("Opening print dialog...", "info");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const storeOptions = [
    { value: "", label: "All Locations" },
    ...stores.map((store) => ({
      value: String(store.id),
      label: store.name,
    })),
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title={
            <div className="flex flex-col">
              <div className="flex items-center gap-3 text-primary mb-2">
                <div className="bg-primary/10 rounded-xl p-1">
                  <Receipt className="h-5 w-5" />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary/80">
                  Sales Analytics
                </span>
              </div>
              <span className="text-3xl font-extrabold tracking-tight">
                Sales Invoice Report
              </span>
            </div>
          }
          subtitle="View and analyze all sales invoices, their statuses, and payment progress."
        />
        <PageHeaderActions>
          <Button
            variant="outline"
            size="lg"
            onClick={handleClearFilters}
            className="rounded-xl mr-2"
          >
            <RefreshCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          {hasPermission("report sales invoice list view") && (
            <Button
              variant="secondary"
              size="lg"
              className="rounded-xl shadow-lg shadow-secondary/20 mr-2"
              onClick={handleGenerateReport}
              disabled={generateReportMutation.isPending}
            >
              {generateReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate PDF
                </>
              )}
            </Button>
          )}
          {hasPermission("report sales invoice list view") && (
            <Button
              size="lg"
              className="rounded-xl shadow-lg shadow-primary/20"
              onClick={() => {
                showToast("Loading report data...", "info");
                previewReportMutation.mutate(filters);
              }}
              disabled={previewReportMutation.isPending}
            >
              {previewReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Preview Report
                </>
              )}
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <div className="flex flex-col gap-8">
        {/* Main Filter Panel */}
        <Card className="shadow-none border border-border/50 overflow-hidden transition-all duration-300">
          <CardHeader className="bg-muted/50 border-b pb-4 pt-4 flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-background shadow-sm rounded-lg border">
                <Filter className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-xl">Filtering Criteria</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Define the customer, limits, date range, and statuses
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="text-xs font-semibold rounded-lg"
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </CardHeader>

          {showFilters && (
            <CardContent className="p-8">
              <div className="grid md:grid-cols-4 gap-8">
                {/* Date & Sort */}
                <div className="space-y-6 md:col-span-1">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" /> Date & Sort
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Start Date
                      </Label>
                      <Input
                        type="datetime-local"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="start_date"
                        value={filters.start_date}
                        onChange={handleFilterChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">End Date</Label>
                      <Input
                        type="datetime-local"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="end_date"
                        value={filters.end_date}
                        onChange={handleFilterChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Sort By</Label>
                      <Select
                        value={filters.sort_by}
                        onValueChange={(val) =>
                          setFilters((p) => ({ ...p, sort_by: val }))
                        }
                      >
                        <SelectTrigger className="bg-muted/30 border-border">
                          <SelectValue placeholder="Sort By" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="date">Date</SelectItem>
                          <SelectItem value="total">Total</SelectItem>
                          <SelectItem value="customer">Customer</SelectItem>
                          <SelectItem value="invoice_number">
                            Invoice Number
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Sort Order
                      </Label>
                      <Select
                        value={filters.sort_order}
                        onValueChange={(val) =>
                          setFilters((p) => ({ ...p, sort_order: val }))
                        }
                      >
                        <SelectTrigger className="bg-muted/30 border-border">
                          <SelectValue placeholder="Sort Order" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="asc">Ascending</SelectItem>
                          <SelectItem value="desc">Descending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Entity & Specifics */}
                <div className="space-y-6 md:col-span-1">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Search className="h-4 w-4 text-primary" /> Details
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Invoice Number
                      </Label>
                      <Input
                        type="text"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="invoice_number"
                        value={filters.invoice_number}
                        onChange={handleFilterChange}
                        placeholder="Search by invoice..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Customer</Label>
                      <AsyncReactSelect
                        loadOptions={loadCustomerOptions}
                        defaultOptions
                        value={selectedCustomer}
                        onChange={(opt) => {
                          setSelectedCustomer(opt);
                          setFilters((p) => ({
                            ...p,
                            customer_id: opt?.value || "",
                          }));
                        }}
                        placeholder="Search Customers..."
                        isClearable
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Store / Branch
                      </Label>
                      <ReactSelect
                        options={storeOptions}
                        value={
                          storeOptions.find(
                            (opt) => opt.value === String(filters.store_id || ""),
                          ) ||
                          (filters.store_id &&
                            currentStore?.id &&
                            String(filters.store_id) === String(currentStore.id)
                            ? {
                              value: String(currentStore.id),
                              label: currentStore.name,
                            }
                            : null)
                        }
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            store_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Locations"
                        isClearable={Boolean(filters.store_id)}
                      />
                    </div>
                  </div>
                </div>

                {/* Statuses */}
                <div className="space-y-6 md:col-span-1">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" /> Statuses
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Invoice Status
                      </Label>
                      <Select
                        value={filters.status}
                        onValueChange={(val) =>
                          setFilters((p) => ({ ...p, status: val }))
                        }
                      >
                        <SelectTrigger className="bg-muted/30 border-border">
                          <SelectValue placeholder="All Statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                          <SelectItem value="refunded">Refunded</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Payment Status
                      </Label>
                      <Select
                        value={filters.payment_status}
                        onValueChange={(val) =>
                          setFilters((p) => ({ ...p, payment_status: val }))
                        }
                      >
                        <SelectTrigger className="bg-muted/30 border-border">
                          <SelectValue placeholder="All Payments" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Payments</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="failed">Failed</SelectItem>
                          <SelectItem value="refunded">Refunded</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                          <SelectItem value="on-hold">On-Hold</SelectItem>
                          <SelectItem value="partially-refunded">
                            Partially Refunded
                          </SelectItem>
                          <SelectItem value="partially-paid">
                            Partially Paid
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Amount Filters */}
                <div className="space-y-6 md:col-span-1">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-primary" /> Amount
                    Limits
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Min Total Spent
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="min_total"
                        value={filters.min_total}
                        onChange={handleFilterChange}
                        placeholder="e.g. 1000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Max Total Spent
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="max_total"
                        value={filters.max_total}
                        onChange={handleFilterChange}
                        placeholder="e.g. 50000"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {previewData && (
          <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {summaryStats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* 1. Total Invoices */}
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Invoices
                      </p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {summaryStats.total_invoices}
                      </h3>
                    </div>
                    <div className="p-3 bg-muted text-muted-foreground rounded-xl">
                      <Receipt className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                {/* 2. Average Invoice */}
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Average Invoice
                      </p>
                      <h3 className="text-2xl font-bold text-primary">
                        {currencySymbol}
                        {(
                          parseFloat(summaryStats.average_invoice) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-primary/10 text-primary rounded-xl">
                      <TrendingUp className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                {/* 3. Total Subtotal */}
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Subtotal
                      </p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {currencySymbol}
                        {(
                          parseFloat(summaryStats.total_subtotal) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-blue-100 text-blue-600 rounded-xl">
                      <Calculator className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                {/* 4. Total Discount */}
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Discount
                      </p>
                      <h3 className="text-2xl font-bold text-orange-600">
                        {currencySymbol}
                        {(
                          parseFloat(summaryStats.total_discount) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-orange-100 text-orange-600 rounded-xl">
                      <Percent className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                {/* 5. Total Tax */}
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Tax
                      </p>
                      <h3 className="text-2xl font-bold text-purple-600">
                        {currencySymbol}
                        {(
                          parseFloat(summaryStats.total_tax) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-purple-100 text-purple-600 rounded-xl">
                      <PieChart className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                {/* 6. Grand Total */}
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Grand Total
                      </p>
                      <h3 className="text-2xl font-bold text-primary">
                        {currencySymbol}
                        {(
                          parseFloat(summaryStats.grand_total) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-primary/10 text-primary rounded-xl">
                      <DollarSign className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                {/* 7. Total Paid */}
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Paid
                      </p>
                      <h3 className="text-2xl font-bold text-green-600">
                        {currencySymbol}
                        {(
                          parseFloat(summaryStats.total_paid) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-green-100 text-green-600 rounded-xl">
                      <CheckCircle className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                {/* 8. Total Outstanding */}
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Outstanding
                      </p>
                      <h3 className="text-2xl font-bold text-destructive">
                        {currencySymbol}
                        {(
                          parseFloat(summaryStats.total_outstanding) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-destructive/10 text-destructive rounded-xl">
                      <Activity className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0 overflow-hidden bg-background">
          <DialogHeader className="px-6 py-4 border-b shrink-0 flex flex-row items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Sales Invoice Report
              </DialogTitle>
              <DialogDescription>
                Preview the generated PDF document.
              </DialogDescription>
            </div>
          </DialogHeader>
          <div className="flex-1 bg-muted/30 relative">
            {pdfBlobUrl ? (
              <iframe
                src={pdfBlobUrl}
                className="w-full h-full absolute inset-0 border-0"
                title="PDF Preview"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin mb-4" />
                <p>Generating PDF document...</p>
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t bg-background shrink-0 flex sm:justify-between items-center">
            <div className="text-sm text-muted-foreground hidden sm:block">
              {filters.start_date && filters.end_date
                ? `Period: ${formatDate(filters.start_date)} to ${formatDate(filters.end_date)}`
                : "All-time report"}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={handlePrintPDF}>
                <PrintIcon className="mr-2 h-4 w-4" />
                Print
              </Button>
              <Button onClick={handleDownloadPDF}>
                <Download className="mr-2 h-4 w-4" />
                Download PDF
              </Button>
              <Button variant="secondary" onClick={handleCloseModal}>
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};


